import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { allPeopleSearchActions, selectAdHocSearch } from '@repo/redux/actions/search';
import ResultsDisplay from '@views/Search/ResultsDisplay';
import { useAppContactSelecting, useSaveViewState, useHotkeyHandler } from '@repo/hooks';
import { KeyboardShortcut, Key } from '@repo/components/tooltips';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScrollRestoration } from '@components/utils/use-scroll-restoration';
import { selectSavedViewState, selectSelectedContactBatch } from '@repo/redux/actions/ui';
import EmptyPeople from './EmptyPeople';
import styles from './People.module.scss';
import DelayedLoader from '@components/DelayedLoader';
import { GroupMembershipCount } from '@views/Group/Header';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { selectDuplicatesCollection } from '@repo/redux/reducers/data/duplicates';
import ArchiveSearchBox from '@views/Archive/ArchiveSearchBox';
import { selectArchiveSearch } from '@repo/redux/reducers/data/archiveSearch';
import { ViewID } from '@repo/utils/types';

const viewID: ViewID = 'People';

export const PeopleHeader = () => {
  const navigate = useNavigate();
  const allPeople = useSelector(selectAdHocSearch('allPeopleSearch'));
  const duplicates = useSelector(selectDuplicatesCollection);
  const archive = useSelector(selectArchiveSearch);
  const loc = useLocation();
  const isOnMap = loc.pathname.includes('map');
  const isOnDuplicates = loc.pathname.includes('duplicates');
  const isOnPeople = !isOnMap && !isOnDuplicates && loc.pathname.includes('people');
  const isOnArchive = loc.pathname.includes('archive');

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.defaultPrevented) {
          return;
        }

        if (e.key === '1' && !isOnPeople) {
          navigate('/people');
        } else if (e.key === '2' && !isOnDuplicates) {
          navigate('/people/duplicates');
        } else if (e.key === '3' && !isOnMap) {
          navigate('/people/map');
        } else if (e.key === '4' && !isOnArchive) {
          navigate('/archive');
        }
      },
    },
    [isOnPeople, navigate],
  );

  return (
    <div className={styles.viewHeader}>
      <div className={cn(styles.tab, styles.groupName, isOnPeople && styles.active)}>
        <KeyboardShortcut placement="bottom" shortcut={<Key>1</Key>}>
          <Link to={'/people'} className={styles.tabTitle}>
            People
          </Link>
        </KeyboardShortcut>
      </div>
      <div className={cn(styles.tab, styles.groupName, isOnDuplicates && styles.active)}>
        <KeyboardShortcut placement="bottom" shortcut={<Key>2</Key>}>
          <Link to={'/people/duplicates'} className={styles.tabTitle}>
            Duplicates
          </Link>
        </KeyboardShortcut>
      </div>
      <div className={cn(styles.tab, styles.groupName, isOnMap && styles.active)}>
        <KeyboardShortcut placement="bottom" shortcut={<Key>3</Key>}>
          <Link to={'/people/map'} className={styles.tabTitle}>
            Map
          </Link>
        </KeyboardShortcut>
      </div>
      <div className={cn(styles.tab, isOnArchive && styles.active)}>
        <KeyboardShortcut placement="bottom" shortcut={<Key>4</Key>}>
          <Link to={'/archive'} className={cn(styles.tabTitle)}>
            Archive
          </Link>
        </KeyboardShortcut>
      </div>
      {isOnPeople && !allPeople.results.updated && <DelayedLoader loading />}
      {isOnDuplicates && !duplicates.updated && <DelayedLoader loading />}
      {isOnArchive && !archive.results.updated && <DelayedLoader loading />}
      {isOnArchive && <ArchiveSearchBox />}
    </div>
  );
};

export default function People() {
  const dispatch = useDispatch();
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const [highlightedItem, setHighlightedItem] = useState(savedViewState?.selectedContact);
  const { setTerm, fetchMore, refetchCurrentPage } = allPeopleSearchActions;
  const allPeople = useSelector(selectAdHocSearch('allPeopleSearch'));
  const scrollingContainerRef = useRef(null);
  const viewScrolledAtMount = useScrollRestoration({ viewID, scrollingContainerRef });
  const loadNextPage = useCallback(() => dispatch(fetchMore()), []);
  const defaultHighlightedItem = useMemo(() => highlightedItem, []);

  useSaveViewState(viewID, { selectedContact: highlightedItem, selectedContactBatch }, [
    highlightedItem,
    selectedContactBatch,
  ]);
  useAppContactSelecting({ contact: highlightedItem, source: 'people' });
  useEffect(() => {
    if (allPeople.results.page > 1 && viewScrolledAtMount) {
      dispatch(refetchCurrentPage());
    } else {
      dispatch(setTerm('all', 100, []));
    }
  }, []);

  return (
    <ResultsDisplay
      search={allPeople}
      setHighlightedItem={setHighlightedItem}
      highlightedItem={highlightedItem}
      loadNextPage={loadNextPage}
      ref={scrollingContainerRef}
      title={<PeopleHeader />}
      defaultHighlightedItem={defaultHighlightedItem}
      defaultHighlightedIdx={1}
      emptyStateUI={<EmptyPeople />}
      listHeader={
        allPeople.results.totalResults > 0 ? (
          <GroupMembershipCount count={allPeople.results.totalResults} />
        ) : undefined
      }
    />
  );
}
